import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Heart, MessageCircle, Share2, MoreHorizontal, Edit, Trash2, Pin, PinOff, Globe, Users, Lock, Flag, Link as LinkIcon, Send, ChevronDown, ChevronUp, Loader2, Flame, Diamond } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { playReactionAnimation } from '../utils/reactionAnimations';
import axios from 'axios';
import MediaGrid from './MediaGrid';
import VideoPlayer from './VideoPlayer';
import HumanVerifiedBadge from './HumanVerifiedBadge';
import ShareSheet from './ShareSheet';

const API = '/api';

const PostCard = ({ post, profileHandle, onUpdate, onDelete, isHighlighted = false }) => {
  const { user } = useAuth();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reacting, setReacting] = useState(false);
  const pendingPostBtnRef = useRef(null);
  const [myReaction, setMyReaction] = useState(
    post.user_reaction_type || (post.liked_by_me || post.user_has_liked || post.user_has_reacted ? 'heart' : null)
  );
  const [reactionCounts, setReactionCounts] = useState({
    heart: post.likes_count || post.reactions?.heart || 0,
    fire: post.reactions?.fire || 0,
    diamond: post.reactions?.diamond || 0,
  });
  
  // Comment state
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const isOwner = user?.id === post.creator_id;
  const canEdit = isOwner && new Date(post.can_edit_until) > new Date();
  
  // Get permalink for this post
  const getPermalink = () => {
    return `${window.location.origin}/u/${profileHandle || post.creator?.handle}?p=${post.id}`;
  };
  
  // Handle copy link to this post
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getPermalink());
      toast.success('Link copied!');
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy link');
    }
  };
  
  // Handle share to social platforms
  const handleShareClick = () => {
    setShowShareSheet(true);
  };
  
  const handleShareComplete = (platform) => {
    console.log('Shared to:', platform, 'Post ID:', post.id);
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Privacy icon
  const getPrivacyIcon = () => {
    if (post.privacy === 'public') return <Globe className="w-3 h-3" />;
    if (post.privacy === 'followers') return <Users className="w-3 h-3" />;
    if (post.privacy === 'subscribers') return <Lock className="w-3 h-3" />;
    return null;
  };

  // Privacy label
  const getPrivacyLabel = () => {
    if (post.privacy === 'public') return 'Public';
    if (post.privacy === 'followers') return 'Followers';
    if (post.privacy === 'subscribers') return 'Subscribers';
    return '';
  };

  // Handle reaction
  const handleReact = async (reactionType = 'heart') => {
    if (!user) {
      toast.error('Please sign in to react');
      return;
    }
    if (reacting) return;
    setReacting(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to react');
        setReacting(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('target_type', 'creator_post');
      formData.append('target_id', post.id);
      formData.append('reaction_type', reactionType);

      const response = await axios.post(`${API}/react`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setMyReaction(response.data.reaction_type);
      if (response.data.counts) {
        setReactionCounts(response.data.counts);
      }

      // Animate only on add/switch
      if ((response.data.action === 'added' || response.data.action === 'updated') && pendingPostBtnRef.current) {
        playReactionAnimation(pendingPostBtnRef.current, reactionType);
      }
      pendingPostBtnRef.current = null;
    } catch (error) {
      console.error('[PostCard] Reaction error:', error);
      if (error.response?.status === 401) {
        toast.error('Please sign in to react');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to react');
      }
    } finally {
      setReacting(false);
    }
  };

  // Load comments
  const loadComments = async () => {
    if (loadingComments) return;
    setLoadingComments(true);
    
    try {
      const response = await axios.get(`${API}/creator/posts/${post.id}/comments?limit=20`);
      console.log('[PostCard] Comments loaded:', response.data);
      setComments(response.data || []);
    } catch (error) {
      console.error('[PostCard] Error loading comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  // Toggle comments visibility
  const toggleComments = () => {
    if (!showComments && comments.length === 0) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  // Submit comment
  const submitComment = async () => {
    if (!user) {
      toast.error('Please sign in to comment');
      return;
    }
    
    const content = newComment.trim();
    if (!content) {
      toast.error('Comment cannot be empty');
      return;
    }
    
    if (submittingComment) return;
    setSubmittingComment(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to comment');
        setSubmittingComment(false);
        return;
      }
      
      console.log('[PostCard] Submitting comment:', { post_id: post.id, content });
      const response = await axios.post(
        `${API}/creator/posts/${post.id}/comments`,
        { content },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      console.log('[PostCard] Comment response:', response.data);
      
      // Add comment to list
      setComments(prev => [response.data, ...prev]);
      setCommentsCount(prev => prev + 1);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('[PostCard] Error submitting comment:', error);
      if (error.response?.status === 401) {
        toast.error('Please sign in to comment');
      } else if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'Cannot comment on this post');
      } else {
        toast.error('Failed to add comment');
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  // Delete comment
  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/creator/posts/${post.id}/comments/${commentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setComments(prev => prev.filter(c => c.id !== commentId));
      setCommentsCount(prev => Math.max(0, prev - 1));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('[PostCard] Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  // Handle edit
  const handleEdit = async () => {
    if (!editContent.trim()) {
      toast.error('Content cannot be empty');
      return;
    }

    setEditing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/creator/posts/${post.id}`,
        { content: editContent },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      toast.success('Post updated');
      setShowEditDialog(false);
      onUpdate?.(response.data);
    } catch (error) {
      console.error('Edit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to edit post');
    } finally {
      setEditing(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/creator/posts/${post.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Post deleted');
      onDelete?.(post.id);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete post');
      setDeleting(false);
    }
  };

  // Handle pin/unpin
  const handleTogglePin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/creator/posts/${post.id}`,
        { is_pinned: !post.is_pinned },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      toast.success(post.is_pinned ? 'Post unpinned' : 'Post pinned');
      onUpdate?.(response.data);
    } catch (error) {
      console.error('Pin error:', error);
      toast.error('Failed to update post');
    }
  };

  // Handle report
  const handleReport = async () => {
    toast.info('Reporting feature coming soon');
  };

  // Linkify content
  const linkifyContent = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:underline">${url}</a>`);
  };

  return (
    <>
      <Card 
        id={`post-${post.id}`}
        className={`bg-gray-900 border-gray-800 hover:border-gray-700 transition-all ${
          isHighlighted ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-gray-950' : ''
        }`}
        data-testid={`post-card-${post.id}`}
      >
        <CardContent className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <Avatar className="w-10 h-10">
                <AvatarImage src={post.creator?.avatar_url} />
                <AvatarFallback>{post.creator?.display_name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <a href={`/u/${post.creator?.handle}`} className="font-semibold hover:text-orange-500 transition-colors">
                    {post.creator?.display_name || 'Creator'}
                  </a>
                  {post.creator?.human_verified && <HumanVerifiedBadge size="small" />}
                  {post.is_pinned && (
                    <Badge variant="secondary" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                      <Pin className="w-3 h-3 mr-1" />
                      Pinned
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span>{formatTime(post.created_at)}</span>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    {getPrivacyIcon()}
                    <span>{getPrivacyLabel()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* More Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700">
                <DropdownMenuItem onClick={handleCopyLink} className="text-gray-300 hover:text-white">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Copy link
                </DropdownMenuItem>
                {isOwner && canEdit && (
                  <DropdownMenuItem onClick={() => setShowEditDialog(true)} className="text-gray-300 hover:text-white">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Post
                  </DropdownMenuItem>
                )}
                {isOwner && (
                  <>
                    <DropdownMenuItem onClick={handleTogglePin} className="text-gray-300 hover:text-white">
                      {post.is_pinned ? <PinOff className="w-4 h-4 mr-2" /> : <Pin className="w-4 h-4 mr-2" />}
                      {post.is_pinned ? 'Unpin' : 'Pin to Profile'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete} className="text-red-400 hover:text-red-300" disabled={deleting}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Post
                    </DropdownMenuItem>
                  </>
                )}
                {!isOwner && (
                  <DropdownMenuItem onClick={handleReport} className="text-gray-300 hover:text-white">
                    <Flag className="w-4 h-4 mr-2" />
                    Report
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Content */}
          {post.content && (
            <div className="text-white whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: linkifyContent(post.content) }} />
          )}

          {/* Media */}
          {post.media_urls && post.media_urls.length > 0 && (
            <div>
              {post.type === 'video' ? (
                <VideoPlayer src={`${post.media_urls[0]}`} />
              ) : (
                <MediaGrid images={post.media_urls.map(url => `${url}`)} />
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-gray-800" data-testid="post-reaction-buttons">
            {[
              { type: 'heart', Icon: Heart, color: 'red', fill: true, label: 'Love' },
              { type: 'fire', Icon: Flame, color: 'orange', fill: false, label: 'Fire' },
              { type: 'diamond', Icon: Diamond, color: 'cyan', fill: false, label: 'Diamond' },
            ].map(({ type, Icon, color, fill, label }) => {
              const isSelected = myReaction === type;
              const count = reactionCounts[type] || 0;
              return (
                <Button
                  key={type}
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    pendingPostBtnRef.current = e.currentTarget;
                    handleReact(type);
                  }}
                  disabled={reacting}
                  className={`transition-colors ${
                    isSelected ? `text-${color}-500` : 'text-gray-400 hover:text-gray-200'
                  }`}
                  data-testid={`reaction-${type}-btn-${post.id}`}
                  title={label}
                  aria-label={`${label}${isSelected ? ' (selected)' : ''}`}
                  aria-pressed={isSelected}
                >
                  <Icon className={`w-4 h-4 mr-1.5 ${isSelected && fill ? 'fill-current' : ''}`} />
                  {count > 0 && <span>{count}</span>}
                </Button>
              );
            })}

            {post.comments_enabled && (
              <Button 
                variant="ghost" 
                size="sm" 
                className={`text-gray-400 hover:text-blue-500 ${showComments ? 'text-blue-500' : ''}`}
                onClick={toggleComments}
                data-testid={`comment-btn-${post.id}`}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {commentsCount > 0 && <span>{commentsCount}</span>}
                {showComments ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
              </Button>
            )}

            <Button 
              variant="ghost" 
              size="sm" 
              className="text-gray-400 hover:text-green-500"
              onClick={handleShareClick}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {/* Comments Section */}
          {showComments && post.comments_enabled && (
            <div className="space-y-3 pt-3 border-t border-gray-800" data-testid={`comments-section-${post.id}`}>
              {/* Comment Input */}
              <div className="flex gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback>{user?.email?.[0]?.toUpperCase() || '?'}</AvatarFallback>
                </Avatar>
                <div className="flex-1 flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={user ? "Write a comment..." : "Sign in to comment"}
                    disabled={!user || submittingComment}
                    className="bg-gray-800 border-gray-700 text-white text-sm"
                    maxLength={1000}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submitComment();
                      }
                    }}
                    data-testid={`comment-input-${post.id}`}
                  />
                  <Button
                    size="sm"
                    onClick={submitComment}
                    disabled={!user || !newComment.trim() || submittingComment}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid={`submit-comment-${post.id}`}
                  >
                    {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Comments List */}
              {loadingComments ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : comments.length > 0 ? (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex gap-2" data-testid={`comment-${comment.id}`}>
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={comment.user?.avatar_url} />
                        <AvatarFallback>{comment.user?.display_name?.[0]?.toUpperCase() || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 bg-gray-800 rounded-lg px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-white">
                              {comment.user?.display_name || 'User'}
                            </span>
                            <span className="text-xs text-gray-500">{formatTime(comment.created_at)}</span>
                          </div>
                          {(user?.id === comment.user_id || isOwner || user?.role === 'admin') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-gray-500 hover:text-red-400"
                              onClick={() => deleteComment(comment.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-sm text-gray-300 mt-1">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 text-sm py-2">No comments yet. Be the first!</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white min-h-[150px]"
            maxLength={2000}
          />
          <div className="text-xs text-gray-500">
            {editContent.length}/2000
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditDialog(false)} disabled={editing}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={editing || !editContent.trim()}>
              {editing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Sheet */}
      <ShareSheet
        isOpen={showShareSheet}
        onClose={() => setShowShareSheet(false)}
        shareUrl={getPermalink()}
        shareTitle={`Post by ${post.creator?.display_name || 'Creator'}`}
        shareDescription={post.content?.substring(0, 100)}
        onShare={handleShareComplete}
      />
    </>
  );
};

export default PostCard;
